import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type Theme = "light";

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const STORAGE_KEY = "app-theme";

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const applyThemeClass = () => {
  const root = document.documentElement;
  root.classList.remove("dark");
};

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setThemeState] = useState<Theme>("light");

  useEffect(() => {
    applyThemeClass();
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, "light");
    }
  }, [theme]);

  const setTheme = useCallback((_next: Theme) => {
    setThemeState("light");
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState("light");
  }, []);

  const value = useMemo(() => ({ theme, toggleTheme, setTheme }), [theme, toggleTheme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
